import React, { useEffect, useState, useRef } from "react";
import { Drawer, DrawerProps, Button, Popconfirm } from "antd";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./index.styled";

interface IDrawerProps extends DrawerProps {
  title?: string;
  icon?: JSX.Element;
  children: any;
  cancelbuttontxt?: string;
  showCancelButton?: boolean;
  confirmbuttontxt?: string;
  onConfirm?: (ev) => void;
  vehiclePlate?: string;
  tagNumber?: string;
  customrendercancelbutton?: JSX.Element;
  disabled: boolean;
  showConfirmButtonVehicleAlreadyHasTag?: boolean;
  showConfirmButtonTagWasInactivate?: boolean;
  onConfirmVehicleAlreadyHasTag?: (ev) => void;
  onConfirmTagHasInactiveUserAccepted?: (ev) => void;
}

interface IFooterProps {
  cancelbuttontxt?: string;
  vehiclePlate?: string;
  tagNumber?: string;
  showCancelButton?: boolean;
  onClose: (ev) => void;
  confirmbuttontxt?: string;
  onConfirm?: (ev) => void;
  onConfirmVehicleAlreadyHasTag?: (ev) => void;
  onConfirmTagHasInactiveUserAccepted?: (ev) => void;
  customrendercancelbutton?: JSX.Element;
  disabled?: boolean;
  showConfirmButtonVehicleAlreadyHasTag?: boolean;
  showConfirmButtonTagWasInactivate?: boolean;
}

const Footer = (props: IFooterProps) => {
  const YES_MESSAGE = useTranslation({ id: "yes" });
  const NO_MESSAGE = useTranslation({ id: "no" });
  const CONFIRMATION_MESSAGE_ALREADY_HAS_TAG = useTranslation(
    { id: "confirmation_message_that_a_license_plate_is_already_linked_to_the_tag" },
    { tagNumber: props.tagNumber, plate: props.vehiclePlate }
  );
  const CONFIRMATION_MESSAGE_DEACTIVATE_TAG = useTranslation({ id: "confirmation_message_to_permanently_deactivate_the_tag" });
  const [popconfirmOpen, setPopconfirmOpen] = useState(false);
  const prevShowInactivateRef = useRef(props.showConfirmButtonTagWasInactivate);
  const prevShowVehicleRef = useRef(props.showConfirmButtonVehicleAlreadyHasTag);

  useEffect(() => {
    const inactivateChanged = !prevShowInactivateRef.current && props.showConfirmButtonTagWasInactivate;
    const vehicleChanged = !prevShowVehicleRef.current && props.showConfirmButtonVehicleAlreadyHasTag;

    if (inactivateChanged || vehicleChanged) {
      setPopconfirmOpen(true);
    }

    prevShowInactivateRef.current = props.showConfirmButtonTagWasInactivate;
    prevShowVehicleRef.current = props.showConfirmButtonVehicleAlreadyHasTag;
  }, [props.showConfirmButtonTagWasInactivate, props.showConfirmButtonVehicleAlreadyHasTag]);

  const getPopconfirmConfig = () => {
    if (props.showConfirmButtonTagWasInactivate) {
      return {
        overlayStyle: { width: "280px" },
        title: CONFIRMATION_MESSAGE_DEACTIVATE_TAG,
        onConfirm: () => {
          setPopconfirmOpen(false);
          props.onConfirmTagHasInactiveUserAccepted(null);
        },
      };
    }
    if (props.showConfirmButtonVehicleAlreadyHasTag) {
      return {
        overlayStyle: { width: "320px" },
        title: CONFIRMATION_MESSAGE_ALREADY_HAS_TAG,
        onConfirm: () => {
          setPopconfirmOpen(false);
          props.onConfirmVehicleAlreadyHasTag(null);
        },
      };
    }

    return null;
  };

  const popconfirmConfig = getPopconfirmConfig();

  const handleButtonClick = () => {
    if (popconfirmConfig) {
      setPopconfirmOpen(true);
    } else {
      props.onConfirm(null);
    }
  };

  const handlePopconfirmCancel = () => {
    setPopconfirmOpen(false);
  };

  return (
    <S.DrawerFooter>
      <div className="left">
        {props.showCancelButton && !props.customrendercancelbutton && (
          <Button onClick={props.onClose} className="btnCancel" disabled={props.disabled}>
            {props.cancelbuttontxt}
          </Button>
        )}
        {props.customrendercancelbutton !== null && props.customrendercancelbutton}
      </div>
      <div className="right row">
        <div className="btnConfirmVehicleAlreadyHasTag">
          {popconfirmConfig ? (
            <Popconfirm
              open={popconfirmOpen}
              overlayStyle={popconfirmConfig.overlayStyle}
              title={popconfirmConfig.title}
              placement="leftBottom"
              onConfirm={popconfirmConfig.onConfirm}
              onCancel={handlePopconfirmCancel}
              okText={YES_MESSAGE}
              cancelText={NO_MESSAGE}
              disabled={props.disabled}
            >
              <Button type="primary" onClick={handleButtonClick} className="btn" disabled={props.disabled} loading={props.disabled}>
                {props.confirmbuttontxt}
              </Button>
            </Popconfirm>
          ) : (
            <Button type="primary" onClick={props.onConfirm} className="btn" disabled={props.disabled} loading={props.disabled}>
              {props.confirmbuttontxt}
            </Button>
          )}
        </div>
      </div>
    </S.DrawerFooter>
  );
};

Footer.defaultProps = {
  cancelbuttontxt: "cancel",
  showCancelButton: true,
  confirmbuttontxt: "Ok",
  showConfirmButtonVehicleAlreadyHasTag: false,
  showConfirmButtonTagWasInactivate: false,
  disabled: false,
  vehiclePlate: "",
  tagNumber: "",
};

const Title = ({ label }) => {
  return <S.Title>{label}</S.Title>;
};

const TagDrawer = (props: IDrawerProps) => {
  return (
    <Drawer
      {...props}
      title={<Title label={props.title} />}
      footer={
        <Footer
          cancelbuttontxt={props.cancelbuttontxt}
          showCancelButton={props.showCancelButton}
          onClose={props.onClose}
          confirmbuttontxt={props.confirmbuttontxt}
          onConfirm={props.onConfirm}
          customrendercancelbutton={props.customrendercancelbutton}
          disabled={props.disabled}
          showConfirmButtonTagWasInactivate={props.showConfirmButtonTagWasInactivate}
          showConfirmButtonVehicleAlreadyHasTag={props.showConfirmButtonVehicleAlreadyHasTag}
          onConfirmVehicleAlreadyHasTag={props.onConfirmVehicleAlreadyHasTag}
          onConfirmTagHasInactiveUserAccepted={props.onConfirmTagHasInactiveUserAccepted}
          vehiclePlate={props.vehiclePlate}
          tagNumber={props.tagNumber}
        />
      }
    >
      {props.children}
    </Drawer>
  );
};

TagDrawer.defaultProps = {
  title: "Default title",
};

export default TagDrawer;
